import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  Users, 
  Calendar as CalendarIcon, 
  Coins,
  Lock,
  Info,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateAjoGroup = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [showInsufficientDialog, setShowInsufficientDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    group_size: '',
    contribution_amount: '',
    contribution_frequency: 'weekly',
    start_date: null
  });

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/wallet`, { headers: getAuthHeaders() });
        setWalletBalance(res.data.available_balance || 0);
      } catch {}
    };
    fetchWallet();
  }, []);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const calculatePayoutDetails = () => {
    const { group_size, contribution_amount } = formData;
    if (!group_size || !contribution_amount) return null;

    const total = parseInt(group_size) * parseFloat(contribution_amount);
    const lock = total * 0.5;
    const securityHold = parseFloat(contribution_amount) * 2;

    return {
      totalPayout: total,
      lockDeposit: lock,
      securityHold: securityHold
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('NGN', '₦');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.group_size || !formData.contribution_amount || !formData.start_date) {
      toast.error('Please fill all fields');
      return;
    }

    // Hard-block: admin must be able to fund their own lock deposit
    const payout = calculatePayoutDetails();
    if (payout && payout.lockDeposit > walletBalance) {
      setShowInsufficientDialog(true);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ajo/groups`, {
        name: formData.name,
        group_size: parseInt(formData.group_size),
        contribution_amount: parseFloat(formData.contribution_amount),
        contribution_frequency: formData.contribution_frequency,
        start_date: formData.start_date.toISOString()
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Ajo group created successfully!');
      navigate(`/ajo/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const payoutDetails = calculatePayoutDetails();
  const frequencyLabel = formData.contribution_frequency === 'monthly' ? 'Monthly' : 'Weekly';
  const insufficientBalance = payoutDetails && payoutDetails.lockDeposit > walletBalance;
  const shortfall = payoutDetails ? Math.max(0, payoutDetails.lockDeposit - walletBalance) : 0;

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="create-group-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate('/ajo')}
            className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Create Ajo Group</h1>
            <p className="text-sm text-slate-500">Set up a new savings group</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="glass-card mb-4 border-2 border-slate-200 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl font-bold text-[#0B1F3A]">Group Details</CardTitle>
              <CardDescription className="text-slate-700 font-semibold">Set up the basic information for your Ajo group</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#0B1F3A] font-bold text-base flex items-center gap-2">
                  <Users size={18} className="text-[#0B1F3A]" />
                  Group Name
                </Label>
                <p className="text-sm text-slate-600 font-medium -mt-1">Give your savings group a memorable name</p>
                <Input
                  id="name"
                  placeholder="e.g., Office Savings Circle"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="h-14 rounded-xl border-2 border-slate-400 focus:border-[#0B1F3A] text-slate-900 font-semibold text-lg"
                  data-testid="group-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#0B1F3A] font-bold text-base flex items-center gap-2">
                  <Users size={18} className="text-[#0B1F3A]" />
                  Group Size
                </Label>
                <p className="text-sm text-slate-600 font-medium -mt-1">How many members will be in this group?</p>
                <Select 
                  value={formData.group_size} 
                  onValueChange={(value) => handleChange('group_size', value)}
                >
                  <SelectTrigger className="h-14 rounded-xl border-2 border-slate-400 text-slate-900 font-semibold text-lg" data-testid="group-size-select">
                    <SelectValue placeholder="Select group size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4" className="font-bold text-base">4 Members</SelectItem>
                    <SelectItem value="6" className="font-bold text-base">6 Members</SelectItem>
                    <SelectItem value="8" className="font-bold text-base">8 Members</SelectItem>
                    <SelectItem value="10" className="font-bold text-base">10 Members</SelectItem>
                    <SelectItem value="12" className="font-bold text-base">12 Members</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#0B1F3A] font-bold text-base flex items-center gap-2">
                  <CalendarIcon size={18} className="text-[#0B1F3A]" />
                  Contribution Frequency
                </Label>
                <p className="text-sm text-slate-600 font-medium -mt-1">How often will members contribute?</p>
                <Select 
                  value={formData.contribution_frequency} 
                  onValueChange={(value) => handleChange('contribution_frequency', value)}
                >
                  <SelectTrigger className="h-14 rounded-xl border-2 border-slate-400 text-slate-900 font-semibold text-lg" data-testid="contribution-frequency-select">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly" className="font-bold text-base">Weekly</SelectItem>
                    <SelectItem value="monthly" className="font-bold text-base">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contribution" className="text-[#0B1F3A] font-bold text-base flex items-center gap-2">
                  <Coins size={18} className="text-[#0B1F3A]" />
                  {frequencyLabel} Contribution (₦)
                </Label>
                <p className="text-sm text-slate-600 font-medium -mt-1">Amount each member pays per {formData.contribution_frequency === 'monthly' ? 'month' : 'week'}</p>
                <div className="relative">
                  <Coins size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1F3A]" />
                  <Input
                    id="contribution"
                    type="number"
                    placeholder="5000"
                    value={formData.contribution_amount}
                    onChange={(e) => handleChange('contribution_amount', e.target.value)}
                    className="h-14 rounded-xl border-2 border-slate-400 focus:border-[#0B1F3A] text-slate-900 font-semibold text-lg pl-10"
                    data-testid="contribution-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#0B1F3A] font-bold text-base flex items-center gap-2">
                  <CalendarIcon size={18} className="text-[#0B1F3A]" />
                  Start Date
                </Label>
                <p className="text-sm text-slate-600 font-medium -mt-1">When will the first contribution be due?</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-14 rounded-xl border-2 border-slate-400 justify-start text-left font-semibold text-lg"
                      data-testid="start-date-button"
                    >
                      <CalendarIcon size={20} className="mr-2 text-[#0B1F3A]" />
                      {formData.start_date ? (
                        <span className="text-slate-900">{format(formData.start_date, 'PPP')}</span>
                      ) : (
                        <span className="text-slate-500">Pick a start date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => handleChange('start_date', date)}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Calculated Summary */}
          {payoutDetails && (
            <Card className="glass-card mb-4 border-l-4 border-l-emerald-500 shadow-lg" data-testid="payout-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xl font-bold text-[#0B1F3A] flex items-center gap-2">
                  <Info size={22} className="text-emerald-600" />
                  Auto-Calculated Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-100 border-2 border-emerald-300">
                    <span className="font-bold text-emerald-800 text-base">Total Payout per Member</span>
                    <span className="font-black text-emerald-800 text-xl">{formatCurrency(payoutDetails.totalPayout)}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-amber-100 border-2 border-amber-300">
                    <div className="flex items-center gap-2">
                      <Lock size={18} className="text-amber-700" />
                      <span className="font-bold text-amber-800 text-base">Lock Deposit (50%)</span>
                    </div>
                    <span className="font-black text-amber-800 text-xl">{formatCurrency(payoutDetails.lockDeposit)}</span>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-100 border-2 border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-bold text-slate-800 text-base">Security Hold (Early Positions)</span>
                        <p className="text-sm text-slate-600 font-medium mt-1">This amount is temporarily held to ensure all members complete their contributions. It will be gradually released as you progress through the cycle.</p>
                      </div>
                      <span className="font-black text-slate-800 text-xl">{formatCurrency(Math.floor(parseInt(formData.group_size) / 3) * parseFloat(formData.contribution_amount))}</span>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-100 border-2 border-blue-300">
                    <p className="font-bold text-blue-800 text-base">Dynamic Protection System</p>
                    <p className="text-blue-700 font-semibold text-sm mt-1">
                      Security holds decrease as your position advances and are fully released by the midpoint of the group cycle.
                    </p>
                  </div>

                  {/* Available balance + inline insufficient warning */}
                  <div className="p-4 rounded-xl bg-white border-2 border-slate-300" data-testid="available-balance-card">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-700 text-sm">Your Available Balance</span>
                      <span className="font-black text-[#0B1F3A] text-lg">{formatCurrency(walletBalance)}</span>
                    </div>
                  </div>

                  {insufficientBalance && (
                    <div
                      className="p-4 rounded-xl bg-red-50 border-2 border-red-400"
                      data-testid="insufficient-balance-warning"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={22} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-black text-red-700 text-base">
                            Insufficient balance for your lock deposit
                          </p>
                          <p className="text-sm text-red-700 font-semibold mt-1">
                            You need {formatCurrency(payoutDetails.lockDeposit)} to join your own group, but you only have {formatCurrency(walletBalance)} available. Top up at least{' '}
                            <span className="font-black">{formatCurrency(shortfall)}</span> before creating this group.
                          </p>
                          <Button
                            type="button"
                            onClick={() => navigate('/wallet')}
                            className="mt-3 h-10 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
                            data-testid="topup-wallet-btn"
                          >
                            Top Up Wallet
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rules Info */}
          <Card className="glass-card mb-6 border-2 border-[#0B1F3A]/20 bg-[#0B1F3A]/5 shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-[#0B1F3A]">
                  <Users size={24} className="text-white" />
                </div>
                <div>
                  <p className="font-black text-[#0B1F3A] text-lg">How Ajo Works</p>
                  <ul className="mt-3 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold text-lg">•</span>
                      <span className="text-slate-800 font-semibold">Members pay <span className="font-black text-[#0B1F3A]">50% of total payout</span> as lock deposit to join</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold text-lg">•</span>
                      <span className="text-slate-800 font-semibold"><span className="font-black text-[#0B1F3A]">{frequencyLabel}</span> contributions collected from all members</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold text-lg">•</span>
                      <span className="text-slate-800 font-semibold">One member receives the <span className="font-black text-[#0B1F3A]">full payout</span> each {formData.contribution_frequency === 'monthly' ? 'month' : 'week'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold text-lg">•</span>
                      <span className="text-slate-800 font-semibold">Group starts automatically when <span className="font-black text-[#0B1F3A]">all spots are filled</span></span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            className="w-full h-16 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg shadow-xl btn-press disabled:bg-slate-400 disabled:cursor-not-allowed"
            disabled={loading || insufficientBalance}
            data-testid="create-group-submit"
          >
            {loading ? 'Creating...' : insufficientBalance ? 'Insufficient Balance' : 'Create Ajo Group'}
          </Button>
        </form>
      </div>

      {/* Insufficient Balance Hard Block Dialog */}
      <AlertDialog open={showInsufficientDialog} onOpenChange={setShowInsufficientDialog}>
        <AlertDialogContent data-testid="insufficient-balance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 font-black text-xl flex items-center gap-2">
              <AlertTriangle size={22} /> Cannot Create Group
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 mt-2">
                <span className="block text-slate-700 font-semibold">
                  You need <span className="font-black text-red-600">{payoutDetails ? formatCurrency(payoutDetails.lockDeposit) : ''}</span> to join your own group as the first member, but your available balance is only <span className="font-black">{formatCurrency(walletBalance)}</span>.
                </span>
                <span className="block p-3 rounded-xl bg-amber-50 border-2 border-amber-200 text-sm text-amber-800 font-semibold">
                  Please top up at least <span className="font-black">{formatCurrency(shortfall)}</span> and try again.
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-semibold border-2" data-testid="insufficient-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowInsufficientDialog(false); navigate('/wallet'); }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="insufficient-topup-btn"
            >
              Top Up Wallet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default CreateAjoGroup;
